import React, { useState, useEffect } from "react";
import QRCodeStyling from "qr-code-styling";
import { Tab, Tabs, Box, TextField, Button, Slider, Typography, Container, Grid, InputAdornment, FormControl, InputLabel, Select, MenuItem, FormControlLabel, Switch } from "@mui/material";
import { ColorLens, Delete, Upload, Download, Autorenew, Save, Edit } from "@mui/icons-material";
import { styled } from "@mui/system";
import './styles.css';
import { useDispatch } from "react-redux";
import { messageShow, uploadSingle } from "../common/api/actions";
import { saveQRCode, deleteQR } from "./api/mutation";
import { fetchSavedQRs } from "./api/query"
import { getImageSource } from "./api/routes";
import {checkPermission } from "../common/Sidebar/checkPermission"


const StyledTabs = styled(Tabs)({
    '& .MuiTabs-indicator': {
        backgroundColor: '#1976d2',
    },
});

const StyledTab = styled(Tab)({
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '0.9rem',
    minWidth: 72,
    '&.Mui-selected': {
        color: '#1976d2',
    },
});

const ColorPicker = ({ label, value, onChange }) => (
    <TextField
        fullWidth
        label={label}
        type="color"
        value={value}
        onChange={onChange}
        InputProps={{
            startAdornment: (
                <InputAdornment position="start">
                    <ColorLens />
                </InputAdornment>
            ),
        }}
    />
);

const Dropdown = ({ label, value, onChange, options, size, sx }) => (
    <FormControl fullWidth size={size} sx={sx}>
        <InputLabel>{label}</InputLabel>
        <Select value={value} onChange={onChange} label={label}>
            {options.map(option => (
                <MenuItem key={option.value} value={option.value}>
                    {option.label}
                </MenuItem>
            ))}
        </Select>
    </FormControl>
);

const DynamicQRCodeGenerator = () => {
    const dispatch = useDispatch();
    const [tabValue, setTabValue] = useState(0);
    const [selectedQRCodeId, setSelectedQRCodeId] = useState(null);
    const initialState = {
        inputValue: "",
        qrCode: {
            color: "#000000",
            backgroundColor: "#FFFFFF",
            size: 300,
            dotType: "square",
            eyeType: "square",
            imageSize: 0.2,
            shape: "square",
            isGradient: false,
            gradient: {
                type: "linear",
                rotation: 0,
                startColor: "#000000",
                endColor: "#000000",
            },
            isBgGradient: false,
            gradientBg: {
                type: "linear",
                rotation: 0,
                startColor: "#FFFFFF",
                endColor: "#FFFFFF",
            },
        },
        imageFile: null,
        loading: false,
        imagePreview: null,
        qrCodeRef: null,
        qrGenerated: false,
        downloadFormat: "png",
        editingId: null
    }
    const [state, setState] = useState(initialState)
    const [savedQRCodes, setSavedQRCodes] = useState([])
    const [permissions, setPermissions] = useState({canView:false, canEdit:false, canDelete:false, canCreate:false})
    useEffect(() => {
        const fetchPermissions = async () => {
            const userPermissions = await checkPermission("utilities","qrGenerator");
            setPermissions(userPermissions);
        };

        fetchPermissions();
    }, []);

    useEffect(() => {
        if (state.inputValue) {
            generateQRCode();
        }
    }, [state.qrCode, state.inputValue, state.imageFile]);

    const handleChange = (key, value) => {
        setState(prevState => ({
            ...prevState,
            [key]: value,
        }));
    };

    const handleQRCodeChange = (key, value) => {
        setState(prevState => ({
            ...prevState,
            qrCode: {
                ...prevState.qrCode,
                [key]: value,
            },
        }));
    };

    const handleGradientChange = (key, value) => {
        setState(prevState => ({
            ...prevState,
            qrCode: {
                ...prevState.qrCode,
                gradient: {
                    ...prevState.qrCode.gradient,
                    [key]: value,
                },
            },
        }));
    };

    const handleBgGradientChange = (key, value) => {
        setState(prevState => ({
            ...prevState,
            qrCode: {
                ...prevState.qrCode,
                gradientBg: {
                    ...prevState.qrCode.gradientBg,
                    [key]: value,
                },
            },
        }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            handleChange("imageFile", file);
            handleChange("imagePreview", URL.createObjectURL(file));
        }
    };

    const handleImageRemove = () =>{
        handleChange("imageFile", null)
        handleChange("imagePreview",null)
    }

    const handleDownload = () => {
        if (state.qrCodeRef) {
            state.qrCodeRef.download({ extension: state.downloadFormat });
            dispatch(messageShow("QR Code downloaded successfully!"));
        }
    };
    useEffect(() => {
        fetchSavedQRCodes();
    }, []);

    useEffect(() => {
        savedQRCodes?.forEach(qrCode => {
            const existingQRCode = document.getElementById(`qrcode-${qrCode._id}`);
            if (existingQRCode && existingQRCode.childNodes.length === 0) {
                const data = qrCode.qrCode
                const qrCodeInstance = new QRCodeStyling({
                    type: "image",
                    shape: data.shape,
                    width: 80,
                    height: 80,
                    data: qrCode.inputValue,
                    image: qrCode.imageFile ? getImageSource(qrCode.imageFile): null,
                    dotsOptions: { color: data.color,type: data.dotType,
                        ...(data.isGradient && {
                            gradient: {
                                type: data.gradient.type,rotation: data.gradient.rotation,
                                colorStops: [
                                    { offset: 0, color: data.gradient.startColor },{ offset: 1, color: data.gradient.endColor },
                                ],
                            },
                        }),
                    },
                    cornersSquareOptions: {color: data.frameColor,type: data.eyeType},
                    cornersDotOptions: {color: data.eyeColor,type: data.eyeType},
                    backgroundOptions: {
                        color: data.backgroundColor,
                        ...(data.isBgGradient && {
                            gradient: {
                                type: data.gradientBg.type,rotation: data.gradientBg.rotation,
                                colorStops: [{ offset: 0, color: data.gradientBg.startColor },{ offset: 1, color: data.gradientBg.endColor }],
                            },
                        }),
                    }
                });
                qrCodeInstance.append(existingQRCode);
            }
        });
    }, [savedQRCodes]);

    const fetchSavedQRCodes = async () => {
        try {
            const response = await fetchSavedQRs();
            setSavedQRCodes(response);
        } catch (error) {
            console.error("Error fetching saved QR codes:", error);
        }
    };
    const [isEditing, setIsEditing] = useState(false)
    const loadQRCodeForEditing = (qrCode) => {
        setIsEditing(true)
        handleChange("inputValue", qrCode.inputValue);
        handleChange("qrCode", qrCode.qrCode);
        if (qrCode.imageFile) {
            handleChange("imageFile", qrCode.imageFile );
            handleChange("imagePreview", getImageSource(qrCode.imageFile));
        } else{
            handleChange("imageFile",null)
            handleChange("imagePreview", null)
        }
        handleChange("qrGenerated", false);
        handleChange("editingId", qrCode._id)
        setSelectedQRCodeId(qrCode._id);
    };

    const handleDeleteQR = async(qrCode)=>{
        if(window.confirm(`Do you want to delete QR Code: ${qrCode.inputValue}?`)){
            try{
                const deletedId = qrCode._id;
                const response = await deleteQR(qrCode._id)
                if(response.success){
                    dispatch(messageShow("QR Deleted successfully!"));
                }
                setSavedQRCodes(prevSavedQRCodes => prevSavedQRCodes.filter((qr) => qr._id !== deletedId))
            }
            catch(error){
                console.error(error);
                dispatch(messageShow("QR not deleted"))
            }
        }
    }

    const onUpload = async () => {
        try {
            const data = {};
            if (state.imageFile instanceof File) {
                const file = new FormData();
                file.append("type", "qrcode");
                file.append("file", state.imageFile);
                file.append("name", state.inputValue);
                const response = await uploadSingle(file);
                if (response?.data?.success) {
                    data.imageFile = response.data.file;
                }
            } else if (state.imageFile) {
                data.imageFile = state.imageFile;
            }

            return data;
        } catch (error) {
            console.error("Error in onUpload:", error);
            throw error;
        }
    };

    const resetForm = ()=>{
        if(window.confirm("Do you want to reset the current QR Settings?")){
            setState(initialState);
            setSelectedQRCodeId(null)
            setIsEditing(false)
            setTabValue(0)
        }
    }


    const handleSave = async () => {
        const qrCodeSettings = { inputValue: state.inputValue, qrCode: state.qrCode, imageFile: ""};
        try {
            const { imageFile } = await onUpload(); 
            qrCodeSettings.imageFile = imageFile;
            let data;
            if (state.editingId)   data = await saveQRCode(qrCodeSettings, state.editingId)
            else    data = await saveQRCode(qrCodeSettings);
            dispatch(messageShow(data.message));
            fetchSavedQRCodes();
        } catch (error) {
            console.error("Error in handleSave:", error);
            dispatch(messageShow("An error occurred while saving the QR code."));
        }
    };

    const generateQRCode = async () => {
        if (!state.inputValue) {
            return;
        }

        handleChange("loading", true);
        setTimeout(async () => {
            const existingQRCode = document.getElementById("qrcode");
            if (existingQRCode.firstChild) existingQRCode.removeChild(existingQRCode.firstChild);

            // Handle image source based on whether it's a File object or URL
            let imageSource ;
            try{
                if (state.imageFile instanceof File) {
                    imageSource = URL.createObjectURL(state.imageFile);
                } else if (typeof state.imageFile === 'string') {
                    imageSource = getImageSource(state.imageFile);
                }
                const qrCode = new QRCodeStyling({
                    type: "image",
                    shape: state.qrCode.shape,
                    width: state.qrCode.size,
                    height: state.qrCode.size,
                    data: state.inputValue,
                    image: imageSource,
                    dotsOptions: {
                        color: state.qrCode.color,
                        type: state.qrCode.dotType,
                        ...(state.qrCode.isGradient && {
                            gradient: {
                                type: state.qrCode.gradient.type,
                                rotation: state.qrCode.gradient.rotation,
                                colorStops: [
                                    { offset: 0, color: state.qrCode.gradient.startColor },
                                    { offset: 1, color: state.qrCode.gradient.endColor },
                                ],
                            },
                        }),
                    },
                    cornersSquareOptions: {
                        color: state.qrCode.frameColor,
                        type: state.qrCode.eyeType,
                    },
                    cornersDotOptions: {
                        color: state.qrCode.eyeColor,
                        type: state.qrCode.eyeType,
                    },
                    backgroundOptions: {
                        color: state.qrCode.backgroundColor,
                        ...(state.qrCode.isBgGradient && {
                            gradient: {
                                type: state.qrCode.gradientBg.type,
                                rotation: state.qrCode.gradientBg.rotation,
                                colorStops: [
                                    { offset: 0, color: state.qrCode.gradientBg.startColor },
                                    { offset: 1, color: state.qrCode.gradientBg.endColor },
                                ],
                            },
                        }),
                    },
                    imageOptions: {
                        crossOrigin: "anonymous",
                        imageSize: state.qrCode.imageSize,
                    },
                });
    
                qrCode.append(existingQRCode);
                handleChange("qrCodeRef", qrCode);
                handleChange("qrGenerated", true);
                handleChange("loading", false);
            }catch(error){
                console.error("Error generating QR Code")
            }
        });
    };


    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    return (
        <Container maxWidth="xl" className="container" sx={{ mt: 4, mb: 4 }}>
            <Grid container spacing={4} sx={{ flexDirection: { xs: 'column', md: 'row' } }}>
                <Grid item xs={12} md={5} lg={4} sx={{ order: { xs: 2, md: 1 } }}>
                        <StyledTabs value={tabValue} onChange={handleTabChange} variant="fullWidth">
                            <StyledTab label="Content" disabled={isEditing ? !permissions.canEdit : !permissions.canCreate}/>
                            <StyledTab label="Design" disabled={isEditing ? !permissions.canEdit : !permissions.canCreate}/>
                            <StyledTab label="Logo" disabled={isEditing ? !permissions.canEdit : !permissions.canCreate}/>
                        </StyledTabs>
                        <Box sx={{ pt: 3 }}>
                            {tabValue === 0 && (
                                <TextField
                                    fullWidth
                                    label="Enter the URL text to generate QR"
                                    variant="outlined"
                                    value={state.inputValue}
                                    onChange={(e) => handleChange("inputValue", e.target.value)}
                                    inputProps={{ maxLength: 200 }}
                                    helperText={`${state.inputValue.length}/200 characters`}
                                    sx={{ mb: 3 }}
                                    disabled={isEditing ? !permissions.canEdit : !permissions.canCreate}
                                    />
                            )}

                            {tabValue === 1 && (
                                <Box>
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: "#333", margin: "10px" }}>
                                                Shape Settings
                                            </Typography>
                                            <Box className="color-picker-container">
                                                <Grid container spacing={2}>
                                                    <Grid item xs={6}>
                                                        <Dropdown
                                                            label="QR Shape"
                                                            value={state.qrCode.shape}
                                                            onChange={(e) => handleQRCodeChange("shape", e.target.value)}
                                                            options={[
                                                                { value: "square", label: "Square" },
                                                                { value: "circle", label: "Circle" },
                                                            ]}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <Dropdown
                                                            label="Dot Type"
                                                            value={state.qrCode.dotType}
                                                            onChange={(e) => handleQRCodeChange("dotType", e.target.value)}
                                                            options={[
                                                                { value: "square", label: "Square" },
                                                                { value: "dots", label: "Dots" },
                                                                { value: "rounded", label: "Rounded" },
                                                                { value: "extra-rounded", label: "Extra Rounded" },
                                                                { value: "classy", label: "Classy" },
                                                                { value: "classy-rounded", label: "Classy Rounded" },
                                                            ]}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: "#333", margin: "10px" }}>
                                                Color Settings
                                            </Typography>
                                            <Grid container spacing={3}>
                                                <Grid item xs={12}>
                                                    <Box className="color-picker-container">
                                                        <Grid container spacing={2}>
                                                            <Grid item xs={12}>
                                                                <FormControlLabel
                                                                    control={<Switch checked={state.qrCode.isGradient} onChange={(e) => handleQRCodeChange("isGradient", e.target.checked)} color="primary" />}
                                                                    label="Use Gradient for Foreground"
                                                                />
                                                            </Grid>
                                                            {!state.qrCode.isGradient && (
                                                                <Grid item xs={12}>
                                                                    <ColorPicker label="Foreground Color" value={state.qrCode.color} onChange={(e) => handleQRCodeChange("color", e.target.value)} />
                                                                </Grid>
                                                            )}
                                                            {state.qrCode.isGradient && (
                                                                <>
                                                                    <Grid item xs={12}>
                                                                        <Dropdown
                                                                            label="Gradient Type"
                                                                            value={state.qrCode.gradient.type}
                                                                            onChange={(e) => handleGradientChange("type", e.target.value)}
                                                                            options={[
                                                                                { value: "linear", label: "Linear" },
                                                                                { value: "radial", label: "Radial" },
                                                                            ]}
                                                                        />
                                                                    </Grid>
                                                                    <Grid item xs={12}>
                                                                        <ColorPicker label="Start Color" value={state.qrCode.gradient.startColor} onChange={(e) => handleGradientChange("startColor", e.target.value)} />
                                                                    </Grid>
                                                                    <Grid item xs={12}>
                                                                        <ColorPicker label="End Color" value={state.qrCode.gradient.endColor} onChange={(e) => handleGradientChange("endColor", e.target.value)} />
                                                                    </Grid>
                                                                    {state.qrCode.gradient.type === "linear" && (
                                                                        <Grid item xs={12}>
                                                                            <Typography gutterBottom>Rotation (degrees)</Typography>
                                                                            <Slider
                                                                                value={state.qrCode.gradient.rotation}
                                                                                onChange={(e, newValue) => handleGradientChange("rotation", newValue)}
                                                                                min={0}
                                                                                max={360}
                                                                                valueLabelDisplay="auto"
                                                                            />
                                                                        </Grid>
                                                                    )}
                                                                </>
                                                            )}
                                                        </Grid>
                                                    </Box>
                                                </Grid>

                                                <Grid item xs={12}>
                                                    <Box className="color-picker-container">
                                                        <Grid container spacing={2}>
                                                            <Grid item xs={12}>
                                                                <FormControlLabel
                                                                    control={<Switch checked={state.qrCode.isBgGradient} onChange={(e) => handleQRCodeChange("isBgGradient", e.target.checked)} color="primary" />}
                                                                    label="Use Gradient for Background"
                                                                />
                                                            </Grid>
                                                            {!state.qrCode.isBgGradient && (
                                                                <Grid item xs={12}>
                                                                    <ColorPicker
                                                                        label="Background Color"
                                                                        value={state.qrCode.backgroundColor}
                                                                        onChange={(e) => handleQRCodeChange("backgroundColor", e.target.value)}
                                                                    />
                                                                </Grid>
                                                            )}


                                                            {state.qrCode.isBgGradient && (
                                                                <>
                                                                    <Grid item xs={12}>
                                                                        <Dropdown
                                                                            label="Background Gradient Type"
                                                                            value={state.qrCode.gradientBg.type}
                                                                            onChange={(e) => handleBgGradientChange("type", e.target.value)}
                                                                            options={[
                                                                                { value: "linear", label: "Linear" },
                                                                                { value: "radial", label: "Radial" },
                                                                            ]}
                                                                        />
                                                                    </Grid>
                                                                    <Grid item xs={12}>
                                                                        <ColorPicker
                                                                            label="Background Start Color"
                                                                            value={state.qrCode.gradientBg.startColor}
                                                                            onChange={(e) => handleBgGradientChange("startColor", e.target.value)}
                                                                        />
                                                                    </Grid>
                                                                    <Grid item xs={12}>
                                                                        <ColorPicker
                                                                            label="Background End Color"
                                                                            value={state.qrCode.gradientBg.endColor}
                                                                            onChange={(e) => handleBgGradientChange("endColor", e.target.value)}
                                                                        />
                                                                    </Grid>
                                                                    {state.qrCode.gradientBg.type === "linear" && (
                                                                        <Grid item xs={12}>
                                                                            <Typography gutterBottom>Rotation (degrees)</Typography>
                                                                            <Slider
                                                                                value={state.qrCode.gradientBg.rotation}
                                                                                onChange={(e, newValue) => handleBgGradientChange("rotation", newValue)}
                                                                                min={0}
                                                                                max={360}
                                                                                valueLabelDisplay="auto"
                                                                            />
                                                                        </Grid>
                                                                    )}
                                                                </>
                                                            )}
                                                        </Grid>
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: "#333", margin: "10px" }}>
                                                Eye Settings
                                            </Typography>
                                            <Box className="color-picker-container">
                                                <Grid container spacing={2}>
                                                    <Grid item xs={12}>
                                                        <Dropdown
                                                            label="Eye Shape"
                                                            value={state.qrCode.eyeType}
                                                            onChange={(e) => handleQRCodeChange("eyeType", e.target.value)}
                                                            options={[
                                                                { value: "square", label: "Square" },
                                                                { value: "rounded", label: "Rounded" },
                                                                { value: "dot", label: "Dot" },
                                                            ]}
                                                        />
                                                    </Grid>
                                                    <Grid container item xs={12} spacing={3} className="eye-settings-container">
                                                        <Grid item xs={12}>
                                                            <ColorPicker label="Eye Color" value={state.qrCode.eyeColor} onChange={(e) => handleQRCodeChange("eyeColor", e.target.value)} />
                                                        </Grid>
                                                        <Grid item xs={12}>
                                                            <ColorPicker label="Eye Frame Color" value={state.qrCode.frameColor} onChange={(e) => handleQRCodeChange("frameColor", e.target.value)} />
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                        </Grid>
                                    
                                </Box>
                            )}
                            {tabValue === 2 && (
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: "#333" }}>
                                            Logo Settings
                                        </Typography>
                                        {state.imagePreview ? (
                                            <Box sx={{ textAlign: "center" }}>
                                                <img
                                                    src={state.imagePreview}
                                                    alt="Preview"
                                                    style={{
                                                        width: "150px",
                                                        height: "150px",
                                                        objectFit: "contain",
                                                        borderRadius: "8px",
                                                        border: "2px solid #eee",
                                                    }}
                                                />
                                                <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
                                                    <Button
                                                        variant="outlined"
                                                        component="label"
                                                        startIcon={<Autorenew />}
                                                        fullWidth
                                                    >
                                                        Replace
                                                        <input type="file" hidden accept="image/*" onChange={handleImageChange} />
                                                    </Button>
                                                    <Button color="error" variant="outlined" onClick={handleImageRemove} fullWidth startIcon={<Delete />}>
                                                        Remove
                                                    </Button>
                                                </Box>
                                            </Box>
                                        ) : (
                                            <Button
                                                variant="outlined"
                                                component="label"
                                                fullWidth
                                                startIcon={<Upload />}
                                                sx={{ py: 2 }}
                                            >
                                                Upload Logo
                                                <input
                                                    type="file"
                                                    hidden
                                                    accept="image/*"
                                                    onChange={handleImageChange}
                                                />
                                            </Button>
                                        )}
                                        <Slider
                                            value={state.qrCode.imageSize}
                                            onChange={(e, newValue) => handleQRCodeChange("imageSize", newValue)}
                                            min={0.1}
                                            max={0.85}
                                            step={0.05}
                                            valueLabelDisplay="auto"
                                            sx={{ mt: 3 }}
                                            valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
                                        />
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: "#666",
                                                display: "block",
                                                textAlign: "center",
                                            }}
                                        >
                                            Logo Size
                                        </Typography>
                                    </Grid>
                                </Grid>
                            )}
                        </Box>
                </Grid>

                <Grid item xs={12} md={7} lg={8}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 4 }}>
                        QR Code Preview
                    </Typography>
                    <div style={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        minWidth: "auto",
                        minHeight: "300px",
                        backgroundColor: "white",
                        padding: "20px",
                        borderRadius: "12px",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    }}>
                        {
                            state.inputValue ? (
                                <div id="qrcode"></div>
                            ) : (
                                <Typography variant="body1" sx={{ color: "#999" }}>
                                    Please enter a URL to generate a QR code.
                                </Typography>
                            )
                        }

                        {state.qrGenerated && state.inputValue ? (
                            <Box sx={{ mt: 4, width: "100%", maxWidth: "420px" }}>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={12} sm={6}>
                                        <Dropdown
                                            label="Download Format"
                                            value={state.downloadFormat}
                                            onChange={(e) => handleChange("downloadFormat", e.target.value)}
                                            options={[
                                                { value: "png", label: "PNG" },
                                                { value: "jpeg", label: "JPEG" },
                                                { value: "svg", label: "SVG" },
                                            ]}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            fullWidth
                                            startIcon={<Download />}
                                            onClick={handleDownload}
                                            sx={{ height: '56px', whiteSpace: 'nowrap' }}
                                        >
                                            Download
                                        </Button>
                                    </Grid>
                                </Grid>
                                <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        fullWidth
                                        startIcon={<Save />}
                                        onClick={handleSave}
                                        sx={{ minWidth: 0, whiteSpace: 'nowrap' }}
                                    >
                                        Save
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="warning"
                                        fullWidth
                                        onClick={resetForm}
                                        sx={{ minWidth: 0, whiteSpace: 'nowrap' }}
                                    >
                                        Reset
                                    </Button>
                                </Box>
                            </Box>
                        ) : (<></>)}
                    </div>
                </Grid>
            </Grid>
            <Grid item xs={12}>
    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 , mt:2}}>
        Saved QR Codes
    </Typography>
    {savedQRCodes.length > 0 ? (
        <Grid container spacing={2}>
            {savedQRCodes.map((qrCode) => (
                <Grid item xs={12} sm={6} md={6} lg={4} xl={3} key={qrCode._id} sx={{ display: 'flex' }}>
                    <Box
                        sx={{
                            border: "1px solid #ccc",
                            borderRadius: "8px",
                            padding: "12px",
                            mb: 2,
                            width: "100%",
                            minHeight: '120px',
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 1.5,
                            backgroundColor: selectedQRCodeId === qrCode._id ? '#e0f7fa' : 'white',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            overflow: 'hidden',
                        }}
                    >
                        <Box sx={{ width: '90px', height: '90px', flexShrink: 0, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <div id={`qrcode-${qrCode._id}`}></div>
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
                            <Typography
                                variant="body2"
                                title={qrCode.inputValue}
                                sx={{
                                    mb: 1,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    fontWeight: 500,
                                }}
                            >
                                {qrCode.inputValue}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, mt: 'auto', flexWrap: 'wrap' }}>
                                <Button 
                                    variant="outlined" 
                                    onClick={() => loadQRCodeForEditing(qrCode)} 
                                    startIcon={<Edit />} 
                                    size="small" 
                                    sx={{ minWidth: '70px', flex: 1, whiteSpace: 'nowrap', px: 1 }}
                                    disabled={!permissions?.canEdit}
                                >
                                    Edit
                                </Button>
                                <Button 
                                    variant="outlined" 
                                    onClick={() => handleDeleteQR(qrCode)} 
                                    startIcon={<Delete />} 
                                    size="small" 
                                    color="error"
                                    sx={{ minWidth: '70px', flex: 1, whiteSpace: 'nowrap', px: 1 }}
                                    disabled={!permissions?.canDelete}
                                >
                                    Delete
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                </Grid>
            ))}
        </Grid>
    ) : (
        <Typography variant="body1" sx={{ color: "#999" }}>
            No saved QR codes found.
        </Typography>
    )}
</Grid>


        </Container>
    );
};

export default DynamicQRCodeGenerator;